import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { fetchPartidoById } from "./usePartidoApi";

const DEFAULT_PARTIDO = {
    aguadaStats: [],
    adversarioStats: [],
    totalAguadaStats: {},
    totalAdversarioStats: {},
    jueces: [],
};

const getStageLabel = (partido) => {
    if (partido?.playoffsEtapa) {
        return `${partido.playoffsEtapa} Partido ${partido.fechaLiga}`;
    }
    if (partido?.temporadaRegular) {
        return `Temp. Reg. Fecha ${partido.fechaLiga}`;
    }
    if (partido?.superLiga) {
        return `SuperLiga Fecha ${partido.fechaLiga}`;
    }
    if (partido?.liguilla) {
        return `Liguilla Fecha ${partido.fechaLiga}`;
    }
    if (partido?.reclasificatorio) {
        return `Reclasificatorio Fecha ${partido.fechaLiga}`;
    }
    if (partido?.fase) {
        return `${partido.fase} Partido ${partido.fechaLiga}`;
    }

    return partido?.fechaLiga ? `Fecha ${partido.fechaLiga}` : "";
};

const formatPartidoDateAndTime = (fecha, hora) => {
    const formattedDate = fecha ? moment(fecha).format("DD/MM/YYYY") : "-";

    if (hora === null || hora === undefined) {
        return {
            date: formattedDate,
            time: "-",
        };
    }

    const horaString = String(hora).trim();
    if (!horaString) {
        return {
            date: formattedDate,
            time: "-",
        };
    }

    const parsedHour = moment(horaString, ["HH:mm", "H:mm", "HH:mm:ss", "H:mm:ss"], true);
    const normalizedHour = parsedHour.isValid() ? parsedHour.format("HH:mm") : horaString;

    return {
        date: formattedDate,
        time: normalizedHour,
    };
};

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const parseClockToSeconds = (value) => {
    if (typeof value !== "string" || !value.includes(":")) {
        return 0;
    }

    const [minutesRaw, secondsRaw] = value.split(":");
    const minutes = Number(minutesRaw);
    const seconds = Number(secondsRaw);

    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) {
        return 0;
    }

    const normalizedSeconds = seconds > 59 ? Math.floor(seconds / 10) : seconds;
    return (minutes * 60) + normalizedSeconds;
};

const getPeriodDuration = (period) => (period > 4 ? 300 : 600);

const getAbsolutePeriod = (item) => {
    const period = toNumber(item?.period);
    if (!period) return 0;
    return item?.periodType === "OVERTIME" ? 4 + period : period;
};

const getElapsedFromPbpItem = (item, maxPeriod) => {
    const period = getAbsolutePeriod(item);
    if (!period || period > maxPeriod) {
        return 0;
    }

    let elapsedBeforeCurrentPeriod = 0;
    for (let index = 1; index < period; index += 1) {
        elapsedBeforeCurrentPeriod += getPeriodDuration(index);
    }

    const periodDuration = getPeriodDuration(period);
    const remainingSeconds = parseClockToSeconds(item?.gt);
    const elapsedInCurrentPeriod = Math.max(0, Math.min(periodDuration, periodDuration - remainingSeconds));

    return elapsedBeforeCurrentPeriod + elapsedInCurrentPeriod;
};

const getStringFromCandidates = (item, candidates = []) => {
    for (const candidate of candidates) {
        const value = item?.[candidate];
        if (typeof value === "string" && value.trim()) {
            return value.trim();
        }
    }

    return "";
};

const normalizeClock = (value) => {
    if (typeof value !== "string" || !value.includes(":")) {
        return "--:--";
    }

    const [minutesRaw, secondsRaw] = value.split(":");
    const minutes = Number(minutesRaw);
    const seconds = Number(secondsRaw);

    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) {
        return "--:--";
    }

    const normalizedSeconds = seconds > 59 ? Math.floor(seconds / 10) : seconds;
    return `${String(minutes).padStart(2, "0")}:${String(Math.max(0, normalizedSeconds)).padStart(2, "0")}`;
};

const getPeriodLabel = (period) => {
    if (period <= 0) {
        return "-";
    }

    if (period <= 4) {
        return `${period}C`;
    }

    return "TE";
};

const getPeriodFilterKey = (period) => {
    if (period <= 0) {
        return "TODOS";
    }

    if (period <= 4) {
        return `${period}C`;
    }

    return "TE";
};

const normalizePbpToken = (value) => {
    if (typeof value !== "string") {
        return "";
    }

    return value.trim().toLowerCase().replace(/[-_\s]+/g, "");
};

const ACTION_TYPE_LABELS = {
    made2pt: "Doble",
    missed2pt: "Doble",
    made3pt: "Triple",
    missed3pt: "Triple",
    madefreethrow: "Tiro libre",
    missedfreethrow: "Tiro libre",
    fieldgoal: "Lanzamiento",
    shot: "Lanzamiento",
    freethrow: "Tiro libre",
    rebound: "Rebote",
    foul: "Falta",
    foulon: "Falta recibida",
    turnover: "Pérdida",
    steal: "Robo",
    block: "Tapa",
    assist: "Asistencia",
    substitution: "Sustitución",
    timeout: "Tiempo muerto",
    jumpball: "Salto entre dos",
    violation: "Violación",
    endperiod: "Fin de período",
    startperiod: "Inicio de período",
};

const SUB_TYPE_LABELS = {
    twopoint: "Doble",
    threepoint: "Triple",
    freethrow: "Tiro libre",
    made: "Convertido",
    missed: "Errado",
    miss: "Errado",
    defensive: "Defensivo",
    offensive: "Ofensivo",
    personal: "Personal",
    technical: "Técnica",
    unsportsmanlike: "Antideportiva",
    flagrant: "Flagrante",
    deadball: "Balón muerto",
    liverebound: "Rebote en juego",
    team: "De equipo",
    bench: "De banca",
    andone: "+1",
    tipin: "Palmeo",
    layup: "Bandeja",
    dunk: "Volcada",
    jumpershot: "Tiro de media distancia",
    hookshot: "Gancho",
    putback: "Segunda oportunidad",
    in: "Ingresa",
    out: "Sale",
};

const QUALIFIER_LABELS = {
    fastbreak: "Contraataque",
    secondchance: "Segunda oportunidad",
    pointsinthepaint: "En la pintura",
    fromturnover: "Tras pérdida",
    assisted: "Con asistencia",
    unassisted: "Sin asistencia",
    andone: "+1",
    putback: "Segunda oportunidad",
    tipin: "Palmeo",
    coasttocoast: "Costa a costa",
    buzzerbeater: "Sobre la chicharra",
    pullup: "En suspensión",
    stepback: "Paso atrás",
    turnaround: "Media vuelta",
    alleyoop: "Alley-oop",
    contested: "Punteado",
    open: "Abierto",
    transition: "Transición",
    onepoint: "1 punto",
    twopoints: "2 puntos",
    threepoints: "3 puntos",
};

const translateActionType = (actionType) => {
    const key = normalizePbpToken(actionType);
    return ACTION_TYPE_LABELS[key] || "Jugada";
};

const translateSubType = (subType) => {
    const key = normalizePbpToken(subType);
    return SUB_TYPE_LABELS[key] || "";
};

const translateQualifier = (qualifier) => {
    const key = normalizePbpToken(qualifier);
    return QUALIFIER_LABELS[key] || "";
};

const getQualifierValues = (item) => {
    const raw = item?.qualifier ?? item?.qualifiers;

    if (Array.isArray(raw)) {
        return raw.map((value) => String(value));
    }

    if (typeof raw === "string" && raw.trim()) {
        return raw.split(",").map((value) => value.trim()).filter(Boolean);
    }

    return [];
};

const resolveShotType = (actionType, subType, qualifierValues = []) => {
    const actionToken = normalizePbpToken(actionType);
    const subTypeToken = normalizePbpToken(subType);
    const qualifierTokens = qualifierValues.map((value) => normalizePbpToken(value)).join(" ");
    const combined = `${actionToken} ${subTypeToken} ${qualifierTokens}`;

    if (combined.includes("free") || combined.includes("freethrow") || combined.includes("libre")) {
        return "Tiro libre";
    }

    if (
        combined.includes("3") ||
        combined.includes("triple") ||
        combined.includes("three")
    ) {
        return "Triple";
    }

    if (
        combined.includes("2") ||
        combined.includes("double") ||
        combined.includes("layup") ||
        combined.includes("dunk") ||
        combined.includes("hook") ||
        combined.includes("jumper")
    ) {
        return "Doble";
    }

    return "Lanzamiento";
};

const resolveShotPoints = (shotType) => {
    if (shotType === "Triple") {
        return 3;
    }

    if (shotType === "Doble") {
        return 2;
    }

    if (shotType === "Tiro libre") {
        return 1;
    }

    return 0;
};

const toPlayerKey = (playerName) => {
    if (typeof playerName !== "string") {
        return "";
    }

    return playerName
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, "");
};

const ensurePlayerTracker = (tracker, playerKey) => {
    if (!tracker[playerKey]) {
        tracker[playerKey] = {
            points: 0,
            rebounds: 0,
            assists: 0,
            steals: 0,
            blocks: 0,
            turnovers: 0,
            fouls: 0,
        };
    }

    return tracker[playerKey];
};

const buildSubstitutionSnapshotLabel = (item, subTypeToken, fallbackPlayer = "") => {
    if (subTypeToken === "in") {
        return "Ingresa";
    }

    if (subTypeToken === "out") {
        return "Sale";
    }

    const playerOut = getStringFromCandidates(item, [
        "playerOut",
        "outPlayer",
        "outgoingPlayer",
        "playerOutName",
        "jugadorSale",
        "sale",
        "out",
    ]);
    const playerIn = getStringFromCandidates(item, [
        "playerIn",
        "inPlayer",
        "incomingPlayer",
        "playerInName",
        "jugadorEntra",
        "entra",
        "in",
    ]);

    const resolvedOut = playerOut || (!playerIn ? fallbackPlayer : "");
    const chunks = [];

    if (resolvedOut) {
        chunks.push("Sale");
    }

    if (playerIn) {
        chunks.push("Ingresa");
    }

    return chunks.join(" • ");
};

const buildPlayerStatSnapshotLabel = (item, actionSummary, playerName, tracker) => {
    if (actionSummary.actionTypeToken === "substitution") {
        return buildSubstitutionSnapshotLabel(item, actionSummary.subTypeToken, playerName);
    }

    if (!playerName) {
        return "";
    }

    const playerKey = toPlayerKey(playerName);
    if (!playerKey) {
        return "";
    }

    const playerStats = ensurePlayerTracker(tracker, playerKey);

    if (actionSummary.isScoringPlay) {
        if (actionSummary.isSuccessful && actionSummary.shotPoints > 0) {
            playerStats.points += actionSummary.shotPoints;
            return `${playerStats.points} PTS`;
        }

        return "";
    }

    switch (actionSummary.actionTypeToken) {
        case "rebound":
            playerStats.rebounds += 1;
            return `${playerStats.rebounds} REB`;
        case "assist":
            playerStats.assists += 1;
            return `${playerStats.assists} AST`;
        case "steal":
            playerStats.steals += 1;
            return `${playerStats.steals} ROB`;
        case "block":
            playerStats.blocks += 1;
            return `${playerStats.blocks} TAP`;
        case "turnover":
            playerStats.turnovers += 1;
            return `${playerStats.turnovers} PER`;
        case "foul":
            playerStats.fouls += 1;
            return `${playerStats.fouls} FAL`;
        default:
            return "";
    }
};

const buildPbpActionSummary = (item) => {
    const actionTypeRaw = getStringFromCandidates(item, ["actionType", "action", "tipo", "playType"]);
    const actionTypeToken = normalizePbpToken(actionTypeRaw);
    const subTypeRaw = getStringFromCandidates(item, ["subType", "subtipo", "detail"]);
    const subTypeToken = normalizePbpToken(subTypeRaw);
    const qualifierValues = getQualifierValues(item);
    const qualifierLabels = [...new Set(qualifierValues.map((value) => translateQualifier(value)).filter(Boolean))];
    const actionTypeLabel = translateActionType(actionTypeRaw) || "Jugada";
    const displayActionTypeLabel = actionTypeLabel === "Jugada" ? "" : actionTypeLabel;
    const subTypeLabel = translateSubType(subTypeRaw);
    const isScoringPlay = Boolean(item?.scoring);
    const isSuccessful = Boolean(item?.success);

    if (actionTypeToken === "endperiod") {
        return {
            actionTypeToken,
            actionTypeLabel,
            subTypeToken,
            subTypeLabel,
            isScoringPlay: false,
            isSuccessful: false,
            shotPoints: 0,
            description: "Final del período",
            detail: "",
        };
    }

    if (actionTypeToken === "substitution") {
        return {
            actionTypeToken,
            actionTypeLabel,
            subTypeToken,
            subTypeLabel,
            isScoringPlay: false,
            isSuccessful: false,
            shotPoints: 0,
            description: "Sustitución",
            detail: "",
        };
    }

    if (isScoringPlay) {
        const shotType = resolveShotType(actionTypeRaw, subTypeRaw, qualifierValues);
        const shotPoints = resolveShotPoints(shotType);
        const shotResult = isSuccessful ? "Convertido" : "Errado";
        const detailBlocks = [displayActionTypeLabel, subTypeLabel, ...qualifierLabels].filter(Boolean);

        return {
            actionTypeToken,
            actionTypeLabel,
            subTypeToken,
            subTypeLabel,
            isScoringPlay,
            isSuccessful,
            shotPoints,
            description: `${shotType} ${shotResult}`,
            detail: detailBlocks.join(" • "),
        };
    }

    const primaryContext = subTypeLabel || qualifierLabels[0] || "";
    const secondaryContext = subTypeLabel ? qualifierLabels : qualifierLabels.slice(1);

    return {
        actionTypeToken,
        actionTypeLabel,
        subTypeToken,
        subTypeLabel,
        isScoringPlay,
        isSuccessful,
        shotPoints: 0,
        description: [actionTypeLabel, primaryContext].filter(Boolean).join(" "),
        detail: secondaryContext.join(" • "),
    };
};

const buildPlayByPlay = (pbp = [], isHomeAguada = true) => {
    if (!Array.isArray(pbp) || !pbp.length) {
        return {
            periodFilters: ["TODOS", "1C", "2C", "3C", "4C"],
            events: [],
        };
    }

    const validEvents = pbp.filter((item) => (
        item &&
        Number.isFinite(Number(item.aguadaScore)) &&
        Number.isFinite(Number(item.adversarioScore))
    ));

    if (!validEvents.length) {
        return {
            periodFilters: ["TODOS", "1C", "2C", "3C", "4C"],
            events: [],
        };
    }

    const orderedEvents = [...validEvents].reverse();
    const maxPeriod = orderedEvents.reduce((max, item) => {
        const period = getAbsolutePeriod(item);
        return period > max ? period : max;
    }, 4);
    const hasOvertime = maxPeriod > 4;
    const periodFilters = hasOvertime
        ? ["TODOS", "1C", "2C", "3C", "4C", "TE"]
        : ["TODOS", "1C", "2C", "3C", "4C"];
    const playerStatsTracker = {};

    const events = orderedEvents
        .map((item, index) => {
            const period = getAbsolutePeriod(item);
            const aguadaScore = toNumber(item?.aguadaScore);
            const rivalScore = toNumber(item?.adversarioScore);
            const homeScore = isHomeAguada ? aguadaScore : rivalScore;
            const awayScore = isHomeAguada ? rivalScore : aguadaScore;
            const elapsed = getElapsedFromPbpItem(item, maxPeriod);
            const actionSummary = buildPbpActionSummary(item);
            const clock = normalizeClock(item?.gt);
            const isGameEnd = clock === "00:00" && index === orderedEvents.length - 1;
            const isPeriodEndCandidate = clock === "00:00" && !isGameEnd;
            const resolvedDescription = (
                isPeriodEndCandidate && (!actionSummary.description || actionSummary.description === "Jugada")
            )
                ? "Final del período"
                : (actionSummary.description || "Jugada");
            const player = getStringFromCandidates(item, [
                "jugador",
                "player",
                "playerName",
                "atleta",
                "autor",
                "name",
                "scoreboardName",
            ]);
            const playerStatSnapshot = buildPlayerStatSnapshotLabel(item, actionSummary, player, playerStatsTracker);
            return {
                id: `pbp-${index}`,
                period,
                periodLabel: getPeriodLabel(period),
                periodKey: getPeriodFilterKey(period),
                clock,
                elapsed,
                description: resolvedDescription,
                detail: actionSummary.detail,
                actionTypeLabel: actionSummary.actionTypeLabel,
                subTypeLabel: actionSummary.subTypeLabel,
                isScoringPlay: actionSummary.isScoringPlay,
                isSuccessful: actionSummary.isSuccessful,
                player,
                playerStatSnapshot,
                scoreLabel: `${homeScore}-${awayScore}`,
                isGameEnd,
            };
        })
        .sort((a, b) => {
            if (a.elapsed === b.elapsed) {
                return b.id.localeCompare(a.id);
            }

            return b.elapsed - a.elapsed;
        });

    return {
        periodFilters,
        events,
    };
};

const buildPbpTrends = (pbp = [], maxAguadaRunFromTotals = 0, maxRivalRunFromTotals = 0) => {
    if (!Array.isArray(pbp) || !pbp.length) {
        return {
            canCalculate: false,
            leadChanges: 0,
            tiedTimes: 0,
            maxAguadaRun: toNumber(maxAguadaRunFromTotals),
            maxRivalRun: toNumber(maxRivalRunFromTotals),
            maxLeadAguada: 0,
            maxLeadRival: 0,
        };
    }

    const validEvents = pbp.filter((item) => (
        item &&
        Number.isFinite(Number(item.aguadaScore)) &&
        Number.isFinite(Number(item.adversarioScore))
    ));

    if (!validEvents.length) {
        return {
            canCalculate: false,
            leadChanges: 0,
            tiedTimes: 0,
            maxAguadaRun: toNumber(maxAguadaRunFromTotals),
            maxRivalRun: toNumber(maxRivalRunFromTotals),
            maxLeadAguada: 0,
            maxLeadRival: 0,
        };
    }

    const orderedEvents = [...validEvents].reverse();
    let leadChanges = 0;
    let tiedTimes = 0;
    let previousNonZeroSign = 0;
    let maxLeadAguada = 0;
    let maxLeadRival = 0;
    let tieOpen = false;

    orderedEvents.forEach((item) => {
        const aguadaScore = toNumber(item.aguadaScore);
        const rivalScore = toNumber(item.adversarioScore);
        const diff = aguadaScore - rivalScore;

        maxLeadAguada = Math.max(maxLeadAguada, diff);
        maxLeadRival = Math.max(maxLeadRival, -diff);

        if (diff === 0 && (aguadaScore !== 0 || rivalScore !== 0)) {
            if (!tieOpen) {
                tiedTimes += 1;
                tieOpen = true;
            }
        } else {
            tieOpen = false;
        }

        const sign = Math.sign(diff);
        if (sign !== 0) {
            if (previousNonZeroSign !== 0 && previousNonZeroSign !== sign) {
                leadChanges += 1;
            }
            previousNonZeroSign = sign;
        }

    });

    return {
        canCalculate: true,
        leadChanges,
        tiedTimes,
        maxAguadaRun: toNumber(maxAguadaRunFromTotals),
        maxRivalRun: toNumber(maxRivalRunFromTotals),
        maxLeadAguada: Math.max(0, maxLeadAguada),
        maxLeadRival: Math.max(0, maxLeadRival),
    };
};

const buildTrendsComparison = (partido) => {
    const aguadaTotals = partido?.totalAguadaStats || {};
    const rivalTotals = partido?.totalAdversarioStats || {};

    return [
        {
            key: "puntosDePerdida",
            label: "Puntos de pérdida",
            aguada: toNumber(aguadaTotals?.puntosDePerdida),
            rival: toNumber(rivalTotals?.puntosDePerdida),
        },
        {
            key: "puntosEnPintura",
            label: "Puntos en pintura",
            aguada: toNumber(aguadaTotals?.puntosEnPintura),
            rival: toNumber(rivalTotals?.puntosEnPintura),
        },
        {
            key: "puntosSegundaOport",
            label: "Puntos 2da oportunidad",
            aguada: toNumber(aguadaTotals?.puntosSegundaOport),
            rival: toNumber(rivalTotals?.puntosSegundaOport),
        },
        {
            key: "puntosContraataque",
            label: "Puntos contraataque",
            aguada: toNumber(aguadaTotals?.puntosContraataque),
            rival: toNumber(rivalTotals?.puntosContraataque),
        },
        {
            key: "puntosBanca",
            label: "Puntos banca",
            aguada: toNumber(aguadaTotals?.puntosBanca),
            rival: toNumber(rivalTotals?.puntosBanca),
        },
        {
            key: "mayorRacha",
            label: "Mayor racha",
            aguada: toNumber(aguadaTotals?.mayorRacha),
            rival: toNumber(rivalTotals?.mayorRacha),
        },
    ];
};

const getNumberFromCandidates = (item, candidates = []) => {
    for (const candidate of candidates) {
        const parsed = Number(item?.[candidate]);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return null;
};

const parseShotMadeValue = (value) => {
    const parsedBoolean = parseBoolean(value);
    if (parsedBoolean !== null) {
        return parsedBoolean;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["made", "success", "scored", "score", "convertido", "acierto", "in", "hit"].includes(normalized)) {
            return true;
        }
        if (["miss", "missed", "failed", "errado", "fallado", "out", "failure"].includes(normalized)) {
            return false;
        }
    }

    return null;
};

const getShotMadeFromCandidates = (item, candidates = []) => {
    for (const candidate of candidates) {
        const parsed = parseShotMadeValue(item?.[candidate]);
        if (parsed !== null) {
            return parsed;
        }
    }

    return null;
};

const parseShotPeriodValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        return Math.trunc(value);
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (!normalized) {
            return null;
        }

        const directParsed = Number(normalized);
        if (Number.isFinite(directParsed) && directParsed > 0) {
            return Math.trunc(directParsed);
        }

        const digitsMatch = normalized.match(/\d+/);
        if (digitsMatch) {
            const parsed = Number(digitsMatch[0]);
            if (Number.isFinite(parsed) && parsed > 0) {
                return Math.trunc(parsed);
            }
        }
    }

    return null;
};

const getShotPeriodFromCandidates = (item, candidates = []) => {
    for (const candidate of candidates) {
        const parsed = parseShotPeriodValue(item?.[candidate]);
        if (parsed !== null) {
            return parsed;
        }
    }

    return null;
};

const buildNormalizedShots = (shots = [], teamKey, teamLabel) => {
    if (!Array.isArray(shots) || !shots.length) {
        return [];
    }

    return shots
        .map((shot, index) => {
            const rawX = getNumberFromCandidates(shot, ["x", "coordX", "locX", "shotX", "px", "left"]);
            const rawY = getNumberFromCandidates(shot, ["y", "coordY", "locY", "shotY", "py", "top"]);

            if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) {
                return null;
            }

            const isMade = getShotMadeFromCandidates(shot, [
                "r",
                "made",
                "success",
                "converted",
                "hit",
                "scored",
                "isMade",
                "result",
                "resultado",
                "status",
                "shotResult",
            ]) ?? false;
            const player = getStringFromCandidates(shot, ["player", "jugador", "name", "nombre", "playerName"]);
            const actionType = getStringFromCandidates(shot, ["actionType", "action", "tipo", "shotType", "tiro"]);
            const normalizedPeriod = getShotPeriodFromCandidates(shot, [
                "per",
                "period",
                "periodo",
                "quarter",
                "cuarto",
                "q",
                "parcial",
                "tempo",
                "periodLabel",
                "quarterLabel",
            ]);

            const periodTypeRaw = (shot?.perType ?? shot?.periodType ?? shot?.period_type ?? "").toString().toUpperCase();
            const absolutePeriod = (normalizedPeriod !== null && periodTypeRaw === "OVERTIME")
                ? 4 + normalizedPeriod
                : normalizedPeriod;

            return {
                id: `${teamKey}-shot-${index}`,
                team: teamKey,
                teamLabel,
                rawX,
                rawY,
                isMade,
                player,
                actionType,
                period: absolutePeriod,
            };
        })
        .filter(Boolean);
};

const normalizeShotsForCourt = (shots = []) => {
    if (!shots.length) {
        return [];
    }

    const xValues = shots.map((shot) => shot.rawX);
    const yValues = shots.map((shot) => shot.rawY);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    const xRange = Math.max(maxX - minX, 1);
    const yRange = Math.max(maxY - minY, 1);

    return shots.map((shot) => ({
        ...shot,
        x: 4 + (((shot.rawX - minX) / xRange) * 92),
        y: 4 + (((maxY - shot.rawY) / yRange) * 88),
    }));
};

const buildShotChartViewModel = (partido, home, away) => {
    const aguadaShots = buildNormalizedShots(partido?.aguadaShots || [], "aguada", "Aguada");
    const rivalShots = buildNormalizedShots(partido?.adversarioShots || [], "rival", partido?.adversario || "Rival");
    const normalizedShots = normalizeShotsForCourt([...aguadaShots, ...rivalShots]);
    const isAguadaHome = Boolean(home?.isAguada);
    const aguadaTotals = isAguadaHome ? (home?.totals || {}) : (away?.totals || {});
    const rivalTotals = isAguadaHome ? (away?.totals || {}) : (home?.totals || {});
    const aguadaMade = toNumber(aguadaTotals?.totalCamposHechos);
    const aguadaAttempted = Math.max(toNumber(aguadaTotals?.totalCamposIntent), 1);
    const rivalMade = toNumber(rivalTotals?.totalCamposHechos);
    const rivalAttempted = Math.max(toNumber(rivalTotals?.totalCamposIntent), 1);

    return {
        shots: normalizedShots,
        teams: {
            aguada: "Aguada",
            rival: partido?.adversario || "Rival",
        },
        isAguadaHome,
        summary: {
            aguadaFgPercent: Math.round((aguadaMade / aguadaAttempted) * 100),
            rivalFgPercent: Math.round((rivalMade / rivalAttempted) * 100),
            pointsInPaint: {
                aguada: toNumber(aguadaTotals?.puntosEnPintura),
                rival: toNumber(rivalTotals?.puntosEnPintura),
            },
            pointsFromTurnovers: {
                aguada: toNumber(aguadaTotals?.puntosDePerdida),
                rival: toNumber(rivalTotals?.puntosDePerdida),
            },
            secondChancePoints: {
                aguada: toNumber(aguadaTotals?.puntosSegundaOport),
                rival: toNumber(rivalTotals?.puntosSegundaOport),
            },
            fastBreakPoints: {
                aguada: toNumber(aguadaTotals?.puntosContraataque),
                rival: toNumber(rivalTotals?.puntosContraataque),
            },
            benchPoints: {
                aguada: toNumber(aguadaTotals?.puntosBanca),
                rival: toNumber(rivalTotals?.puntosBanca),
            },
        },
        hasData: normalizedShots.length > 0,
    };
};

const buildScoreEvolution = (pbp = []) => {
    if (!Array.isArray(pbp) || !pbp.length) {
        return null;
    }

    const validEvents = pbp.filter((item) => (
        item &&
        Number.isFinite(Number(item.aguadaScore)) &&
        Number.isFinite(Number(item.adversarioScore))
    ));

    if (!validEvents.length) {
        return null;
    }

    const orderedEvents = [...validEvents].reverse();
    const maxPeriod = orderedEvents.reduce((max, item) => {
        const period = getAbsolutePeriod(item);
        return period > max ? period : max;
    }, 4);

    const totalDuration = Array.from({ length: maxPeriod }, (_, index) => getPeriodDuration(index + 1))
        .reduce((accumulator, value) => accumulator + value, 0);

    const points = orderedEvents.map((item, index) => {
        const aguada = toNumber(item.aguadaScore);
        const rival = toNumber(item.adversarioScore);

        return {
        id: `evo-${index}`,
        elapsed: getElapsedFromPbpItem(item, maxPeriod),
        aguada,
        rival,
        diff: aguada - rival,
    };
    });

    const lastPoint = points[points.length - 1];
    if (!lastPoint) {
        return null;
    }

    const normalizedPoints = [
        { id: "start", elapsed: 0, aguada: 0, rival: 0, diff: 0 },
        ...points,
        {
            id: "end",
            elapsed: totalDuration,
            aguada: lastPoint.aguada,
            rival: lastPoint.rival,
            diff: lastPoint.diff,
        },
    ];

    const dedupedPoints = normalizedPoints.filter((item, index, source) => {
        if (index === 0) {
            return true;
        }

        const previous = source[index - 1];
        return !(
            previous.elapsed === item.elapsed &&
            previous.aguada === item.aguada &&
            previous.rival === item.rival &&
            previous.diff === item.diff
        );
    });

    const maxScore = dedupedPoints.reduce((max, item) => {
        const candidate = Math.max(item.aguada, item.rival);
        return candidate > max ? candidate : max;
    }, 0);

    const minDiff = dedupedPoints.reduce((min, item) => (item.diff < min ? item.diff : min), 0);
    const maxDiff = dedupedPoints.reduce((max, item) => (item.diff > max ? item.diff : max), 0);
    const maxAbsDiff = Math.max(Math.abs(minDiff), Math.abs(maxDiff), 1);

    const overtimeCount = Math.max(0, maxPeriod - 4);
    const baseLabels = [
        { key: "start", text: "INICIO", elapsed: 0 },
        { key: "q1", text: "Q1", elapsed: 600 },
        { key: "q2", text: "Q2", elapsed: 1200 },
        { key: "q3", text: "Q3", elapsed: 1800 },
    ];
    if (overtimeCount > 0) {
        baseLabels.push({ key: "q4", text: "Q4", elapsed: 2400 });
    }
    const otLabels = Array.from({ length: overtimeCount }, (_, i) => ({
        key: `ot${i + 1}`,
        text: overtimeCount === 1 ? "TE" : `TE${i + 1}`,
        elapsed: 2400 + (i + 1) * 300,
    }));
    const labels = [
        ...baseLabels,
        ...otLabels,
        { key: "final", text: "FINAL", elapsed: totalDuration },
    ];

    return {
        totalDuration,
        maxScore,
        minDiff,
        maxDiff,
        maxAbsDiff,
        maxPeriod,
        points: dedupedPoints,
        labels,
    };
};

const parseBoolean = (value) => {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value === 1;
    }
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "si", "sí", "ganado", "gano", "ganó", "win"].includes(normalized)) {
            return true;
        }
        if (["false", "0", "no", "perdido", "perdio", "perdió", "loss"].includes(normalized)) {
            return false;
        }
    }
    return null;
};

const sumMinutes = (players = []) => {
    const total = players.reduce((accumulator, player) => accumulator + toNumber(player?.minutos), 0);
    return total > 0 ? Math.round(total) : "-";
};

const getTopPlayerByStat = (players = [], key, label) => {
    if (!players.length) {
        return null;
    }

    const topPlayer = players.reduce((best, current) =>
        toNumber(current?.[key]) > toNumber(best?.[key]) ? current : best
    );

    return {
        id: `${key}-${topPlayer.jugador}`,
        name: topPlayer.jugador,
        value: toNumber(topPlayer?.[key]),
        label,
    };
};

function usePartidoData(partidoId) {
    const [partido, setPartido] = useState(DEFAULT_PARTIDO);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadPartido = async () => {
            setIsLoading(true);
            setIsError(false);

            try {
                const data = await fetchPartidoById(partidoId);

                if (!isMounted) {
                    return;
                }

                setPartido({ ...DEFAULT_PARTIDO, ...data });
            } catch (error) {
                if (isMounted) {
                    setIsError(true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (partidoId) {
            loadPartido();
        }

        return () => {
            isMounted = false;
        };
    }, [partidoId]);

    const viewModel = useMemo(() => {
        const aguadaIsHome = Boolean(partido?.local);
        const aguadaName = "Aguada";
        const rivalName = partido?.adversario || "Adversario";
        const aguadaScore = toNumber(partido?.totalAguadaStats?.totalPuntos);
        const rivalScore = toNumber(partido?.totalAdversarioStats?.totalPuntos);
        const parsedGanado = parseBoolean(partido?.ganado);
        const aguadaWon = parsedGanado !== null ? parsedGanado : aguadaScore > rivalScore;

        const home = {
            name: aguadaIsHome ? aguadaName : rivalName,
            isAguada: aguadaIsHome,
            score: aguadaIsHome ? aguadaScore : rivalScore,
            players: aguadaIsHome ? partido?.aguadaStats || [] : partido?.adversarioStats || [],
            totals: aguadaIsHome ? partido?.totalAguadaStats || {} : partido?.totalAdversarioStats || {},
            coach: aguadaIsHome ? partido?.dtAguada : partido?.dtAdversario,
            role: "Local",
            isWinner: aguadaIsHome ? aguadaWon : !aguadaWon,
        };

        const away = {
            name: aguadaIsHome ? rivalName : aguadaName,
            isAguada: !aguadaIsHome,
            score: aguadaIsHome ? rivalScore : aguadaScore,
            players: aguadaIsHome ? partido?.adversarioStats || [] : partido?.aguadaStats || [],
            totals: aguadaIsHome ? partido?.totalAdversarioStats || {} : partido?.totalAguadaStats || {},
            coach: aguadaIsHome ? partido?.dtAdversario : partido?.dtAguada,
            role: "Visitante",
            isWinner: aguadaIsHome ? !aguadaWon : aguadaWon,
        };

        const quarters = (partido?.cuartos || []).map((quarter, index) => {
            const aguadaQuarter = toNumber(
                quarter?.aguadaParcial ?? quarter?.aguadaTotal
            );
            const rivalQuarter = toNumber(
                quarter?.adversarioParcial ?? quarter?.adversarioTotal
            );

            return {
                id: `quarter-${index + 1}`,
                label: index < 4 ? `Q${index + 1}` : `TE${index - 3}`,
                home: home.isAguada ? aguadaQuarter : rivalQuarter,
                away: home.isAguada ? rivalQuarter : aguadaQuarter,
            };
        });

        const hasStats = (partido?.aguadaStats || []).length > 0;
        const aguadaPlayers = partido?.aguadaStats || [];
        const rivalsPlayers = partido?.adversarioStats || [];

        let statusMessage = "";
        if (aguadaScore < 21) {
            statusMessage = "Partido suspendido";
        } else if (!hasStats && aguadaScore > 21) {
            statusMessage = "Estadísticas no recopiladas";
        }

        const referees = (partido?.jueces || []).filter((referee) => referee && referee !== "-").join(", ");

        const partidoDateTime = formatPartidoDateAndTime(partido?.fecha, partido?.hora);

        return {
            liga: partido?.liga || "",
            resultado: partido?.resultado || "",
            stage: getStageLabel(partido),
            date: partidoDateTime.date,
            time: partidoDateTime.time,
            cancha: partido?.cancha || "-",
            referees: referees || "-",
            home,
            away,
            hasStats,
            quarters,
            statusMessage,
            leaders: [
                getTopPlayerByStat(aguadaPlayers, "puntos", "Puntos"),
                getTopPlayerByStat(aguadaPlayers, "rebotesTot", "Rebotes"),
                getTopPlayerByStat(aguadaPlayers, "asistencias", "Asistencias"),
            ].filter(Boolean),
            rivalsLeaders: [
                getTopPlayerByStat(rivalsPlayers, "puntos", "Puntos"),
                getTopPlayerByStat(rivalsPlayers, "rebotesTot", "Rebotes"),
                getTopPlayerByStat(rivalsPlayers, "asistencias", "Asistencias"),
            ].filter(Boolean),
            scoreEvolution: buildScoreEvolution(partido?.pbp || []),
            playByPlay: buildPlayByPlay(partido?.pbp || [], home.isAguada),
            pbpTrends: buildPbpTrends(
                partido?.pbp || [],
                partido?.totalAguadaStats?.mayorRacha,
                partido?.totalAdversarioStats?.mayorRacha
            ),
            trendsComparison: buildTrendsComparison(partido),
            shotChart: buildShotChartViewModel(partido, home, away),
            minutes: {
                home: sumMinutes(home.players),
                away: sumMinutes(away.players),
            },
        };
    }, [partido]);

    return {
        isLoading,
        isError,
        partido,
        viewModel,
    };
}

export default usePartidoData;
